import { FormattedMessage } from 'react-intl';
import Button from '../core/Button';
import { IconExternalLink, IconWarningTriangle, IconXmark } from '@/icons';
import IconButton from '../core/IconButton';
import { cn } from '@/helpers/classnames';
import { useTenantID } from '@/hooks/useTenantID';
import { useAutoAnimateConditional } from '@/hooks/useAutoAnimateConditional';
import type { DuplicateMatch } from '@/hooks/useTravellerDuplicateDetection';

interface DuplicateMatchesListProps {
  matches: DuplicateMatch[];
  departmentId: string;
  onDismiss?: () => void;
  onSelectMatch?: (match: DuplicateMatch) => void;
}

export const DuplicateMatchesList = ({
  matches,
  departmentId,
  onDismiss,
  onSelectMatch,
}: DuplicateMatchesListProps) => {
  const [listRef] = useAutoAnimateConditional<HTMLDivElement>();

  return (
    <div
      className={cn(
        'mb-4 rounded-lg border border-yellow-200 bg-yellow-50 p-4',
        matches.length > 0
          ? 'border-yellow-200 bg-yellow-50'
          : 'border-transparent bg-transparent',
      )}
    >
      {matches.length > 0 && (
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-center gap-2">
            <IconWarningTriangle className="size-5 text-yellow-600" />
            <h3 className="text-yellow-800">
              <FormattedMessage defaultMessage="Möjliga dubbletter hittade" />
            </h3>
          </div>
          {onDismiss && (
            <IconButton
              aria-label="Avfärda varning"
              icon={<IconXmark />}
              onClick={onDismiss}
              variant="ghost"
            />
          )}
        </div>
      )}

      <div ref={listRef} className="space-y-3">
        {matches.map((match) => (
          <DuplicateMatchItem
            key={match.traveller.traveller.id}
            match={match}
            departmentId={departmentId}
            onSelectMatch={onSelectMatch}
          />
        ))}
      </div>
    </div>
  );
};

interface DuplicateMatchItemProps {
  match: DuplicateMatch;
  departmentId: string;
  onSelectMatch?: (match: DuplicateMatch) => void;
}

const DuplicateMatchItem = ({
  match,
  departmentId,
  onSelectMatch,
}: DuplicateMatchItemProps) => {
  const tenantId = useTenantID();
  const { traveller: travellerData, matchedFields } = match;
  const { traveller } = travellerData;
  const { first_name, last_name } = traveller;
  const canSelectMatch =
    !matchedFields.includes('email') ||
    (matchedFields.includes('email') &&
      !travellerData.memberships?.some(
        (membership) => membership.department_id === departmentId,
      ));

  return (
    <div
      key={traveller.id}
      className="flex flex-wrap justify-between gap-3 rounded-md border border-yellow-200 bg-background-50 p-3"
    >
      <div className="flex-1">
        <div className="mb-1 flex items-center gap-2">
          <span
            className={cn(
              'font-medium text-text-900',
              (matchedFields.includes('first_name') ||
                matchedFields.includes('last_name')) &&
                'rounded bg-yellow-100 px-1',
            )}
          >
            {first_name || last_name ? (
              `${first_name || ''}${last_name ? ` ${last_name}` : ''}`
            ) : (
              <FormattedMessage defaultMessage="Anonym resenär" />
            )}
          </span>
        </div>

        <div className="mb-2 text-sm text-text-700">
          {traveller.email && (
            <span
              className={
                matchedFields.includes('email')
                  ? 'rounded bg-yellow-100 px-1'
                  : ''
              }
            >
              {traveller.email}
            </span>
          )}
          {traveller.national_identity_number && (
            <span
              className={
                matchedFields.includes('national_identity_number')
                  ? 'rounded bg-yellow-100 px-1'
                  : ''
              }
            >
              {traveller.national_identity_number}
            </span>
          )}
        </div>

        {!canSelectMatch && (
          <div className="text-sm text-yellow-600">
            <FormattedMessage defaultMessage="Resenären är redan medlem i den valda avdelningen" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1">
        <Button
          variant="ghost"
          onClick={() =>
            window.open(
              `/${tenantId}/travellers/${traveller.id}/details`,
              '_blank',
            )
          }
        >
          <FormattedMessage defaultMessage="Visa" />
          <IconExternalLink />
        </Button>
        <Button
          onClick={() => onSelectMatch?.(match)}
          disabled={!canSelectMatch}
        >
          <FormattedMessage defaultMessage="Välj" />
        </Button>
      </div>
    </div>
  );
};
